import React, { useState, useEffect } from 'react';
import CommentsSection from './CommentsSection';
import api from '../../../utils/api';

const OriginalBookView = ({
  book,
  selectedIdx,
  setSelectedIdx,
  comments,
  newComment,
  setNewComment,
  isLoadingComments,
  postingComment,
  commentError,
  handleCommentSubmit,
  user,
  onGeneratedChaptersChange,
  onViewAllGenerated
}) => {
  const [generatedChapters, setGeneratedChapters] = useState(new Set());
  const [isCheckingChapters, setIsCheckingChapters] = useState(true);
  const [generatedChapterContent, setGeneratedChapterContent] = useState(null);
  const [isGeneratingChapter, setIsGeneratingChapter] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const [showingGenerated, setShowingGenerated] = useState(false);

  useEffect(() => {
    const checkGeneratedChapters = async () => {
      if (!user || !book) return;
      
      try {
        setIsCheckingChapters(true);
        const response = await api.get(`/api/customized-book/content?bookId=${book.id}`);
        if (response.data && response.data.chapters) {
          const generatedIds = new Set(response.data.chapters.map(ch => String(ch.id)));
          setGeneratedChapters(generatedIds);
          if (onGeneratedChaptersChange) {
            onGeneratedChaptersChange(generatedIds);
          }
          
          // If current chapter is generated, load its content
          if (book.chapters && book.chapters[selectedIdx]) {
            const currentChapter = book.chapters[selectedIdx];
            const currentChapterId = String(currentChapter.id);
            if (generatedIds.has(currentChapterId)) {
              const existingChapter = response.data.chapters.find(ch => String(ch.id) === currentChapterId);
              if (existingChapter) {
                setGeneratedChapterContent(existingChapter);
                setShowingGenerated(true);
              }
            } else {
              // Reset if chapter is not generated
              setGeneratedChapterContent(null);
              setShowingGenerated(false);
            }
          }
        }
      } catch (error) {
        console.error('Error checking generated chapters:', error);
      } finally {
        setIsCheckingChapters(false);
      }
    };

    checkGeneratedChapters();
  }, [user, book, selectedIdx]);

  if (!book || !book.chapters || !book.chapters[selectedIdx]) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading book content...</div>
        <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
          Book: {book ? 'exists' : 'null'}, 
          Chapters: {book?.chapters?.length || 0}, 
          SelectedIdx: {selectedIdx}
        </div>
      </div>
    );
  }

  const chapter = book.chapters[selectedIdx];

  const isFirstChapter = selectedIdx === 0;
  const isLastChapter = selectedIdx === book.chapters.length - 1;
  const isChapterGenerated = generatedChapters.has(String(chapter.id));

  const handlePrevious = () => {
    if (!isFirstChapter) {
      setSelectedIdx(selectedIdx - 1);
    }
  };

  const handleNext = () => {
    if (!isLastChapter) {
      setSelectedIdx(selectedIdx + 1);
    }
  };

  const handleGenerateChapter = async () => {
    if (!user) {
      alert('Please log in to generate AI chapters');
      return;
    }

    try {
      setIsGeneratingChapter(true);
      setGenerationError(null);
      
      console.log('Generating chapter for bookId:', book.id, 'chapterId:', chapter.id);
      const response = await api.post(`/api/customized-book/generate-chapter/${book.id}/${chapter.id}`);
      
      console.log('Generation response:', response.data);
      
      if (response.data.success) {
        setGeneratedChapterContent(response.data.chapter);
        setShowingGenerated(true);
        // Update the generated chapters set
        setGeneratedChapters(prev => new Set([...prev, String(chapter.id)]));
      } else {
        setGenerationError('Failed to generate chapter');
      }
    } catch (err) {
      console.error('Error generating chapter:', err);
      const errorMessage = err.response?.data?.error || 'Failed to generate chapter';
      const missingFields = err.response?.data?.missingFields;
      
      if (missingFields && missingFields.length > 0) {
        setGenerationError(`${errorMessage}\n\nMissing settings: ${missingFields.join(', ')}\n\nPlease complete your settings first.`);
      } else {
        setGenerationError(errorMessage);
      }
    } finally {
      setIsGeneratingChapter(false);
    }
  };

  const handleShowGenerated = () => {
    setShowingGenerated(true);
    setGenerationError(null);
  };

  const handleShowOriginal = () => {
    setShowingGenerated(false);
    setGenerationError(null);
  };

  return (
    <div className="book-content-container">
      {/* Chapter Banner Image or Title */}
      {chapter.image_url ? (
        <div 
          className="chapter-header-image"
          style={{
            backgroundImage: `url(${chapter.image_url})`
          }}
        >
          <div className="chapter-header-overlay" />
          <h2 className="book-chapter-title chapter-title">
            {chapter.title}
          </h2>
        </div>
      ) : (
        <h2 className="book-chapter-title" style={{ textAlign: 'center', margin: '32px 0 24px 0' }}>
          {chapter.title}
        </h2>
      )}
      
      {/* Generate AI Chapter Button */}
      {!isCheckingChapters && user && (
        <div style={{ 
          textAlign: 'center', 
          margin: '20px 0',
          padding: '20px',
          borderTop: '1px solid #eee'
        }}>
          {!isChapterGenerated ? (
            // Chapter not generated - show generate button
            <button
              onClick={handleGenerateChapter}
              disabled={isGeneratingChapter}
              style={{
                padding: '12px 24px',
                backgroundColor: isGeneratingChapter ? '#ccc' : '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: isGeneratingChapter ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                if (!isGeneratingChapter) {
                  e.target.style.backgroundColor = '#219a52';
                  e.target.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseOut={(e) => {
                if (!isGeneratingChapter) {
                  e.target.style.backgroundColor = '#27ae60';
                  e.target.style.transform = 'translateY(0)';
                }
              }}
            >
              {isGeneratingChapter ? 'Generating...' : '🤖 Generate Customized Chapter'}
            </button>
          ) : (
            // Chapter is generated - show toggle buttons
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              {!showingGenerated ? (
                <button
                  onClick={handleShowGenerated}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#16a085',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  Show Generated Chapter
                </button>
              ) : (
                <button
                  onClick={handleShowOriginal}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#16a085',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  Show Original Chapter
                </button>
              )}
            <button
              onClick={() => {
                if (onViewAllGenerated) {
                  onViewAllGenerated();
                } else {
                  window.location.href = `/customized-book/${book.id}`;
                }
              }}
                style={{
                  padding: '10px 20px',
                backgroundColor: '#16a085',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                View All Generated Chapters
              </button>
            </div>
          )}
          
          {!isChapterGenerated && (
            <div style={{ 
              fontSize: '14px', 
              color: '#666', 
              marginTop: '8px' 
            }}>
              Get a personalized version of this chapter based on your settings
            </div>
          )}
        </div>
      )}

      {/* Generation Error */}
      {generationError && (
        <div style={{ 
          padding: '15px', 
          margin: '20px 0', 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          border: '1px solid #f5c6cb', 
          borderRadius: '5px',
          textAlign: 'center'
        }}>
          {generationError}
        </div>
      )}
      
      {/* Chapter Content */}
      {showingGenerated && generatedChapterContent ? (
        <div>
          <div style={{ 
            padding: '15px', 
            margin: '20px 0', 
            backgroundColor: '#d4edda', 
            color: '#155724', 
            border: '1px solid #c3e6cb', 
            borderRadius: '5px',
            textAlign: 'center'
          }}>
            ✨ Your personalized version of this chapter
          </div>
          <div
            className="book-preview-chapter-content"
            dangerouslySetInnerHTML={{ __html: generatedChapterContent.content || '<em>No content</em>' }}
            style={{
              lineHeight: '1.6',
              fontSize: '16px'
            }}
          />
        </div>
      ) : (
        <div
          className="book-preview-chapter-content"
          dangerouslySetInnerHTML={{ __html: chapter.content || '<em>No content</em>' }}
        />
      )}
      {/* Navigation Buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px', padding: '0 32px' }}>
        <button
          onClick={handlePrevious}
          disabled={isFirstChapter}
          className="btn-secondary"
          style={{
            opacity: isFirstChapter ? 0.5 : 1,
            cursor: isFirstChapter ? 'not-allowed' : 'pointer'
          }}
        >
          ← Previous
        </button>

        <span style={{ fontSize: '14px', color: '#666' }}>
          Chapter {selectedIdx + 1} of {book.chapters.length}
        </span>

        <button
          onClick={handleNext}
          disabled={isLastChapter}
          className="btn-primary"
          style={{
            opacity: isLastChapter ? 0.5 : 1,
            cursor: isLastChapter ? 'not-allowed' : 'pointer'
          }}
        >
          Next →
        </button>
      </div>
      
      {/* Comments Section */}
      <CommentsSection
        comments={comments}
        newComment={newComment}
        setNewComment={setNewComment}
        isLoadingComments={isLoadingComments}
        postingComment={postingComment}
        commentError={commentError}
        handleCommentSubmit={handleCommentSubmit}
        user={user}
      />
    </div>
  );
};

export default OriginalBookView; 